import { useEffect, useRef } from "react";
import { analytics } from "@/services/analytics.service";
import { analyticsBootstrapService } from "@/services/analyticsBootstrap.service";
import { IS_CONNECTION_SESSION } from "@/utils/connection-session";
import { sanitizeRoute } from "@/utils/analyticsUrl";

interface RouteAnalyticsOptions {
  pathname: string;
  search: string;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const GOOGLE_ANALYTICS_MARKETING_PATHS = new Set([
  "/",
  "/web-landing",
  "/mobile-app-landing",
  "/app-to-save-and-organize-social-media-posts",
  "/save-places-from-tiktok",
  "/organize-saved-reels",
  "/save-tiktoks",
  "/save-recipes-from-instagram-and-tiktok",
  "/save-travel-ideas-from-instagram-and-tiktok",
  "/save-events-from-instagram-and-tiktok",
  "/save-gift-ideas-from-social-media",
  "/recime-alternative",
  "/stacklist-alternative",
  "/compare",
]);

function normalizePathname(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

function shouldTrackGoogleAnalyticsPageview(pathname: string, isAuthenticated: boolean): boolean {
  if (isAuthenticated) return false;
  const normalizedPathname = normalizePathname(pathname);
  return (
    GOOGLE_ANALYTICS_MARKETING_PATHS.has(normalizedPathname) ||
    normalizedPathname === "/blog" ||
    normalizedPathname.startsWith("/blog/")
  );
}

export function useRouteAnalytics({ pathname, search, isAuthenticated, isLoading }: RouteAnalyticsOptions) {
  const authRef = useRef(isAuthenticated);

  useEffect(() => {
    authRef.current = isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    if (isLoading) return;
    if (IS_CONNECTION_SESSION) return;

    const route = sanitizeRoute(pathname, search);
    analytics.capture("$pageview", {
      path: route.pathname,
      search: route.search,
      is_authenticated: authRef.current,
    });

    if (shouldTrackGoogleAnalyticsPageview(pathname, authRef.current)) {
      analyticsBootstrapService.trackGoogleAnalyticsPageView(route);
    }
  }, [pathname, search, isLoading]);
}
